/**
 * Device capability detection
 * V1.1: Detect WebGPU, memory, platform for smart routing
 */
// Cache settings
const CACHE_KEY = 'browserfirst.deviceProfile.v1';
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// In-memory cache for session
let sessionCache = null;
/**
 * Detect platform info from user agent.
 */
function detectPlatform() {
    if (typeof navigator === 'undefined') {
        return { os: 'unknown' };
    }
    const ua = navigator.userAgent;
    const platform = { ua };
    // Detect OS
    if (/Mac OS X|macOS/i.test(ua)) {
        platform.os = 'macos';
    }
    else if (/Windows/i.test(ua)) {
        platform.os = 'windows';
    }
    else if (/Android/i.test(ua)) {
        platform.os = 'android';
        platform.isMobile = true;
    }
    else if (/iPhone|iPad|iPod/i.test(ua)) {
        platform.os = 'ios';
        platform.isMobile = true;
    }
    else if (/Linux/i.test(ua)) {
        platform.os = 'linux';
    }
    else {
        platform.os = 'unknown';
    }
    // Detect mobile (broader check)
    if (!platform.isMobile) {
        platform.isMobile = /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    }
    // Detect browser
    if (/Edg\//i.test(ua)) {
        platform.browser = 'edge';
    }
    else if (/Chrome/i.test(ua)) {
        platform.browser = 'chrome';
    }
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
        platform.browser = 'safari';
    }
    else if (/Firefox/i.test(ua)) {
        platform.browser = 'firefox';
    }
    return platform;
}
/**
 * Detect memory info from available APIs.
 */
function detectMemory() {
    const memory = {};
    // navigator.deviceMemory (RAM in GB, coarse buckets: 0.25, 0.5, 1, 2, 4, 8)
    // Only available in Chrome/Edge, and only over HTTPS
    if (typeof navigator !== 'undefined' && 'deviceMemory' in navigator) {
        memory.deviceMemoryGB = navigator.deviceMemory;
    }
    // performance.memory (Chrome only, non-standard)
    if (typeof performance !== 'undefined' && 'memory' in performance) {
        const perfMemory = performance.memory;
        if (perfMemory?.jsHeapSizeLimit) {
            memory.jsHeapLimitMB = Math.round(perfMemory.jsHeapSizeLimit / (1024 * 1024));
        }
    }
    return memory;
}
/**
 * Compute inference hints based on device capabilities.
 * This uses conservative heuristics - we cannot measure VRAM directly.
 */
function computeInferenceHints(memory, platform, adapterInfo) {
    const notes = [];
    let recommendedMaxModelSizeMB;
    const deviceMemoryGB = memory.deviceMemoryGB;
    // Base heuristic on deviceMemory
    if (deviceMemoryGB !== undefined) {
        if (deviceMemoryGB <= 4) {
            recommendedMaxModelSizeMB = 500;
            notes.push(`Device reports ${deviceMemoryGB}GB RAM. Recommending small models only.`);
        }
        else if (deviceMemoryGB <= 8) {
            recommendedMaxModelSizeMB = 1200;
            notes.push(`Device reports ${deviceMemoryGB}GB RAM. Medium models should work.`);
        }
        else if (deviceMemoryGB <= 16) {
            recommendedMaxModelSizeMB = 2500;
            notes.push(`Device reports ${deviceMemoryGB}GB RAM. Larger models may work.`);
        }
        else {
            recommendedMaxModelSizeMB = 4000;
            notes.push(`Device reports ${deviceMemoryGB}GB RAM. Large models likely supported.`);
        }
    }
    else {
        // No deviceMemory available - use conservative defaults with platform hints
        if (platform.isMobile) {
            recommendedMaxModelSizeMB = 500;
            notes.push('Mobile device detected. Using conservative model size limit.');
        }
        else if (platform.os === 'macos') {
            // macOS with unified memory often has good GPU support
            recommendedMaxModelSizeMB = 1500;
            notes.push('macOS detected. Assuming reasonable GPU memory.');
        }
        else {
            // Desktop but unknown memory - be somewhat conservative
            recommendedMaxModelSizeMB = 1200;
            notes.push('Could not detect device memory. Using conservative default.');
        }
    }
    // Boost for known good GPU vendors
    if (adapterInfo?.vendor) {
        const vendor = adapterInfo.vendor.toLowerCase();
        if (vendor.includes('apple')) {
            // Apple Silicon has unified memory, often generous
            if (recommendedMaxModelSizeMB < 2000) {
                recommendedMaxModelSizeMB = Math.max(recommendedMaxModelSizeMB, 1500);
                notes.push('Apple GPU detected. Unified memory may allow larger models.');
            }
        }
        else if (vendor.includes('nvidia')) {
            notes.push('NVIDIA GPU detected. VRAM-dependent performance.');
        }
        else if (vendor.includes('amd')) {
            notes.push('AMD GPU detected.');
        }
        else if (vendor.includes('intel')) {
            // Intel integrated graphics - be conservative
            if (recommendedMaxModelSizeMB > 1000) {
                recommendedMaxModelSizeMB = 1000;
                notes.push('Intel integrated graphics detected. Using conservative limit.');
            }
        }
    }
    // Mobile penalty
    if (platform.isMobile && recommendedMaxModelSizeMB > 600) {
        recommendedMaxModelSizeMB = 600;
        notes.push('Mobile device: reduced model size limit.');
    }
    notes.push('These are heuristics. Actual VRAM cannot be measured from the browser.');
    return { recommendedMaxModelSizeMB, notes };
}
/**
 * Generate a stable-ish fingerprint for caching.
 * Not meant to be unique per user, just stable per device class.
 */
function generateFingerprint(adapterInfo, memory, platform) {
    const parts = [];
    parts.push(adapterInfo?.vendor || 'unknown');
    parts.push(adapterInfo?.architecture || 'unknown');
    parts.push(String(memory.deviceMemoryGB || 'unknown'));
    parts.push(platform.browser || 'unknown');
    parts.push(platform.os || 'unknown');
    return parts.join(':');
}
/**
 * Try to load cached device profile from localStorage.
 */
function loadCachedProfile() {
    if (typeof localStorage === 'undefined') {
        return null;
    }
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached)
            return null;
        const parsed = JSON.parse(cached);
        // Check version and expiry
        if (parsed.version !== CACHE_VERSION)
            return null;
        if (Date.now() > parsed.expiresAt)
            return null;
        return parsed.profile;
    }
    catch {
        return null;
    }
}
/**
 * Save device profile to localStorage.
 */
function saveCachedProfile(profile) {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        const cached = {
            profile,
            version: CACHE_VERSION,
            expiresAt: Date.now() + CACHE_TTL_MS,
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
    }
    catch {
        // Ignore storage errors
    }
}
/**
 * Detect WebGPU adapter info.
 */
async function detectWebGPU() {
    if (typeof navigator === 'undefined' || !navigator.gpu) {
        return { supported: false };
    }
    try {
        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter) {
            return { supported: false };
        }
        const info = adapter.info;
        const adapterInfo = {};
        if (info) {
            if (info.vendor)
                adapterInfo.vendor = info.vendor;
            if (info.architecture)
                adapterInfo.architecture = info.architecture;
            if (info.description)
                adapterInfo.description = info.description;
            if (info.device)
                adapterInfo.device = info.device;
        }
        return {
            supported: true,
            adapterInfo: Object.keys(adapterInfo).length > 0 ? adapterInfo : undefined,
        };
    }
    catch {
        return { supported: false };
    }
}
/**
 * Detect device capabilities.
 * Results are cached in memory for the session and in localStorage for 7 days.
 *
 * @param forceRefresh - If true, bypass cache and re-detect
 */
export async function detectDevice(forceRefresh = false) {
    // Return session cache if available
    if (!forceRefresh && sessionCache) {
        return sessionCache;
    }
    // Try localStorage cache
    if (!forceRefresh) {
        const cached = loadCachedProfile();
        if (cached) {
            sessionCache = cached;
            return cached;
        }
    }
    // Detect fresh
    const platform = detectPlatform();
    const memory = detectMemory();
    const webgpu = await detectWebGPU();
    const inferenceHints = computeInferenceHints(memory, platform, webgpu.adapterInfo);
    const fingerprint = generateFingerprint(webgpu.adapterInfo, memory, platform);
    const profile = {
        webgpuSupported: webgpu.supported,
        adapterInfo: webgpu.adapterInfo,
        platform,
        memory,
        inferenceHints,
        fingerprint,
        ts: Date.now(),
    };
    // Cache it
    sessionCache = profile;
    saveCachedProfile(profile);
    return profile;
}
/**
 * Clear cached device profile.
 */
export function clearDeviceCache() {
    sessionCache = null;
    if (typeof localStorage !== 'undefined') {
        try {
            localStorage.removeItem(CACHE_KEY);
        }
        catch {
            // Ignore
        }
    }
}
//# sourceMappingURL=detect.js.map